import type { ApiClientError } from '~/composables/api/api'

const TRANSIENT_API_ERROR_KINDS = new Set<ApiClientError['kind']>([
  'network',
  'timeout',
  'cancelled',
  'rate-limit',
])

const TRANSIENT_ERROR_MESSAGES = new Set([
  'failed to fetch',
  'fetch failed',
  'network error',
  'networkerror when attempting to fetch resource.',
  'load failed',
  'the user aborted a request.',
])

type ErrorLike = {
  cause?: unknown
  code?: string
  error?: unknown
  isApiClientError?: boolean
  kind?: ApiClientError['kind']
  message?: string
  name?: string
  status?: number
  statusCode?: number
}

/**
 * Errors caused by a temporary loss of connectivity should stay in query state rather than
 * becoming global Vue errors. VPN changes can reject many concurrent requests at once.
 */
export function isTransientApiError(error: unknown): boolean {
  const visited = new Set<unknown>()
  let current = error

  while (current && !visited.has(current)) {
    visited.add(current)

    if (typeof current !== 'object') return false

    const candidate = current as ErrorLike
    if (
      candidate.isApiClientError === true &&
      candidate.kind &&
      TRANSIENT_API_ERROR_KINDS.has(candidate.kind)
    ) {
      return true
    }

    if (
      candidate.status === 408 ||
      candidate.statusCode === 408 ||
      candidate.status === 429 ||
      candidate.statusCode === 429 ||
      candidate.status === 504 ||
      candidate.statusCode === 504
    )
      return true

    if (
      candidate.name === 'AbortError' ||
      candidate.name === 'TimeoutError' ||
      candidate.code === 'ERR_NETWORK' ||
      candidate.code === 'ERR_CANCELED' ||
      candidate.code === 'ECONNABORTED'
    ) {
      return true
    }

    const message = candidate.message?.trim().toLowerCase()
    if (message && (TRANSIENT_ERROR_MESSAGES.has(message) || message.includes('timeout'))) {
      return true
    }

    current = candidate.cause ?? candidate.error
  }

  return false
}
